'use client';

import * as React from 'react';
import { CarouselOptions } from 'types/sharedComponents';
import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import CarouselPagination from 'components/shared/Carousel/CarouselPagination';
import useCarousel from 'hooks/useCarousel';
import { useEffect, useState } from 'react';
import CarouselNumericPagination from 'components/shared/Carousel/CarouselNumericPagination';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/styles';
import { OverflowVisibleSettings } from 'types/constants';

export enum PaginationType {
  Manual = 'Manual',
  Numeric = 'Numeric',
}

export interface BaseCarouselProps {
  totalItems: number;
  carouselClassName: string;
  carouselContentClassName: string;
  carouselPaginationClassName: string;
  children: React.ReactNode;
  showPaginationThreshold?: number;
  carouselOpts?: CarouselOptions;
  paginationType?: PaginationType;

  // Optional: provide a portal target without using document.querySelector
  paginationPortal?: Element | null;
  paginationPortalRef?: React.RefObject<Element | null>;
}

export function BaseCarousel({
  totalItems,
  carouselClassName,
  carouselContentClassName,
  carouselPaginationClassName,
  children,
  showPaginationThreshold = 3,
  carouselOpts = {
    align: 'start',
    slidesToScroll: 1,
    containScroll: false,
  },
  paginationType = PaginationType.Manual,
  paginationPortal,
  paginationPortalRef,
  ...props
}: BaseCarouselProps) {
  const [isClient, setIsClient] = useState(false);
  const { current, scrollTo, setApi } = useCarousel();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const Pagination = paginationType === PaginationType.Numeric ? CarouselNumericPagination : CarouselPagination;
  // eslint-disable-next-line react-hooks/refs -- reading ref during render to determine portal target
  const externalPortal = paginationPortalRef?.current ?? paginationPortal ?? null;

  return (
    <Carousel setApi={setApi} className={carouselClassName} opts={carouselOpts} {...props}>
      <CarouselContent className={carouselContentClassName} overflowVisible={OverflowVisibleSettings.All}>
        {children}
        {totalItems > showPaginationThreshold && isClient && (
          <>
            <Pagination
              className={cn(
                externalPortal?.attributes.getNamedItem('data-root-classname')?.value,
                carouselPaginationClassName,
              )}
              total={totalItems}
              current={current}
              onSelect={scrollTo}
              testId="root"
            />
            {externalPortal &&
              createPortal(
                <Pagination
                  className={cn(carouselPaginationClassName)}
                  total={totalItems}
                  current={current}
                  onSelect={scrollTo}
                  testId="portaled"
                />,
                externalPortal,
              )}
          </>
        )}
      </CarouselContent>
    </Carousel>
  );
}
